import { useEffect, useState } from 'react';

import { useDataProvider } from 'react-admin';

import type { DomainLicense } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

interface UseDomainLicenseResult {
  license: DomainLicense | undefined;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Alternative to 'useGetOne': allows fetching the license via realm metadata
 * without requiring a mandatory resource ID.
 */
export const useDomainLicense = (domainId: Identifier | undefined): UseDomainLicenseResult => {
  const dataProvider = useDataProvider();
  const [license, setLicense] = useState<DomainLicense | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (domainId === undefined) {
      return;
    }

    const fetchLicense = async (): Promise<void> => {
      try {
        const result = await dataProvider.getOne('license', {
          id: undefined,
          meta: { realmId: domainId },
        });
        setLicense(result.data.license as DomainLicense);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchLicense();
  }, [dataProvider, domainId]);

  return { license, isLoading, error };
};
